/** Day view — a horizontal hour-axis timeline. Rows = employees; each scheduled
 *  shift is drawn as a positioned bar across the 06:00–24:00 axis. Click a row's
 *  empty track to add a shift; click a bar to edit it. */
import { Users } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '../../../components/ui/cn.js';
import { EmptyState } from '../../../components/ui/Feedback.js';
import { Chip } from '../../../components/ui/StatusPill.js';
import { type ShiftRow, type StaffRow } from '../hooks/useStaff.js';

import {
  barGeometry,
  DAY_END_HOUR,
  DAY_START_HOUR,
  formatHours,
  roleTone,
  sameDay,
  shiftHours,
  startOfDay,
  toTimeInput,
} from './scheduleUtils.js';

const SEGMENTS = Array.from(
  { length: (DAY_END_HOUR - DAY_START_HOUR) / 2 },
  (_, i) => DAY_START_HOUR + i * 2,
);

export function DayView({
  anchor,
  staff,
  shifts,
  onAdd,
  onEdit,
}: {
  anchor: Date;
  staff: StaffRow[];
  shifts: ShiftRow[];
  onAdd: (staffId: string, date: Date) => void;
  onEdit: (shift: ShiftRow) => void;
}): ReactNode {
  const { t } = useTranslation();
  const day = startOfDay(anchor);

  if (staff.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-surface p-8">
        <EmptyState icon={Users} title={t('staff.schedule.no_staff')} />
      </div>
    );
  }

  function dayShifts(staffId: string): ShiftRow[] {
    return shifts
      .filter(
        (sh) =>
          sh.staff_id === staffId &&
          sh.scheduled_start != null &&
          sameDay(new Date(sh.scheduled_start), day),
      )
      .sort((a, b) => (a.scheduled_start! < b.scheduled_start! ? -1 : 1));
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-surface">
      <div className="min-w-[760px]">
        {/* Hour axis */}
        <div className="flex border-b border-border bg-surface-2">
          <div className="w-[180px] shrink-0 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted">
            {t('staff.title')}
          </div>
          <div className="flex flex-1">
            {SEGMENTS.map((h) => (
              <div key={h} className="flex-1 border-l border-border px-1 py-1.5 text-xs text-muted">
                {String(h).padStart(2, '0')}:00
              </div>
            ))}
          </div>
        </div>

        {/* Employee rows */}
        {staff.map((s) => {
          const rows = dayShifts(s.id);
          const total = rows.reduce(
            (h, sh) => h + shiftHours(sh.scheduled_start, sh.scheduled_end),
            0,
          );
          return (
            <div key={s.id} className="flex border-b border-border last:border-b-0">
              <div className="w-[180px] shrink-0 px-3 py-2">
                <div className="truncate font-medium text-fg">
                  {s.first_name} {s.last_name}
                </div>
                <div className="text-xs text-muted">
                  {t('staff.roles.' + s.app_role)}
                  {total > 0 ? ` · ${formatHours(total)} ${t('staff.schedule.hours_short')}` : ''}
                </div>
              </div>
              <div
                role="button"
                tabIndex={0}
                onClick={() => onAdd(s.id, day)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onAdd(s.id, day);
                }}
                className="relative min-h-[52px] flex-1 cursor-pointer hover:bg-surface-2"
              >
                {/* gridlines */}
                <div className="pointer-events-none absolute inset-0 flex">
                  {SEGMENTS.map((h) => (
                    <div key={h} className="flex-1 border-l border-border" />
                  ))}
                </div>
                {/* shift bars */}
                {rows.map((sh) => {
                  const { leftPct, widthPct } = barGeometry(sh.scheduled_start!, sh.scheduled_end!);
                  return (
                    <button
                      key={sh.id}
                      data-testid={`shift-${sh.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(sh);
                      }}
                      style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                      className={cn('absolute bottom-1.5 top-1.5 overflow-hidden')}
                    >
                      <Chip
                        tone={roleTone(s.app_role)}
                        className="h-full w-full justify-start whitespace-nowrap font-mono text-[11px]"
                      >
                        {toTimeInput(sh.scheduled_start!)}–{toTimeInput(sh.scheduled_end!)}
                      </Chip>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
